import _pick from 'lodash/pick'
import _isEqual from 'lodash/isEqual'
import { CounselingTypeEnum } from 'app/Bookings/constants'
import HeaderFilter from 'app/ProviderSearch/HeaderFilter'
import ProviderFilter from 'app/ProviderSearch/ProviderFilter'
import { getFilterValueFromId } from 'app/ProviderDetails/utils'
import ProvidersList from 'app/ProviderSearch/ProvidersAndMap/ProvidersList'
import ProvidersListWaterfall from 'app/ProviderSearch/ProvidersAndMap/ProvidersListWaterfall'
import {
  AvailabilityFilterValues,
  SECONDARY_FILTERS,
  LOW_AVAILABILITY_MAX_PROVIDERS,
  LOW_AVAILABILITY_WITH_FEW_SLOTS_MAX_PROVIDERS,
  LOW_AVAILABILITY_MAX_SLOTS_PER_PROVIDER,
  WATERFALL_MOUNT_MAX_DIRECT_MATCHES,
} from 'app/ProviderSearch/constants'
import { getInitialPartialBudget } from 'app/ProviderSearch/ProvidersAndMap/ProvidersListWaterfall/waterfallBudget'
import {
  groupFilters,
  getFilterLookup,
  getRequiredGenderFilters,
  mergeGenderFiltersWithStateAvailability,
} from 'app/ProviderSearch/utils'
import classNames from 'classnames'
import AvailabilityLegend from 'common/AvailabilityLegend'
import Navbar from 'common/Navbar'
import PageLoader from 'common/PageLoader'
import Prenav from 'common/Prenav'
import ResetSupportBooking from 'common/ResetSupportBooking'
import ScrollHorizontally from 'common/ScrollHorizontally'
import ShowHideMapButton from 'common/ShowHideMapButton'
import HeadComponent from 'components/common/HeadComponent'
import { PAGINATION_LIMIT } from 'constants/api'
import {
  COOKIE_KEYS,
  INTAKE_BOOKING_WINDOW_HOURS,
  LOCATION_FETCH_STATUS,
  searchNumber,
  sessionStorageKeys,
  SLOTS_ROW_LIMIT_V3,
  supportDashboardToProviderSearchKeys,
} from 'constants/common'
import { RudderEvent } from 'constants/rudderEvents'
import ProviderSlotContext, {
  getProviderSlots,
} from 'context/ProviderBooking/ProviderSlots'
import SlotDateContext, { slotDate } from 'context/ProviderBooking/SlotDate'
import MobileFilterModalProvider from 'context/ProviderSearch/MobileFilterModalProvider'
import AllFiltersProvider from 'context/ProviderSearch/AllFiltersProvider'
import useTimeZoneContext, {
  TimeZoneContext,
} from 'context/common/TimeZoneContext'
import {
  ProviderFilterTypes,
  getFilters,
  getFiltersByTypeAndState,
} from 'dataSource/ProviderSearch'
import useSalesforceLead from 'hooks/salesforce/useSalesforceLead'
import useShownSlots from 'hooks/slots/useShownSlots'
import useB2bDetailsByKeyword from 'hooks/useB2bDetailsByKeyword'
import useBreakpoint from 'hooks/useBreakPoint'
import useFetchLocation from 'hooks/useFetchLocation'
import useHeaderScrollAnimate from 'hooks/useHeaderScrollAnimate'
import useInitGlobalFilter from 'hooks/useInitGlobalFilter'
import useThriveAuth from 'hooks/useThriveAuth'
import _isEmpty from 'lodash/isEmpty'
import _omit from 'lodash/omit'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { animated } from 'react-spring'
import { getParsedQuery, hashSfAddress, modifyURLQuery } from 'utils/common'
import logEvent from 'utils/logEvent'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import useExtendedBookingTreatment from 'hooks/treatments/useExtendedBookingTreatment'
import useWaterfallSimplification from 'hooks/treatments/useWaterfallSimplification'
import {
  hasSecondaryFilters,
  shouldEnableDowOnlyWaterfall,
} from 'app/ProviderSearch/utils'
import styles from './index.module.scss'
import useSalesforceCase from 'hooks/salesforce/useSalesforceCase'
import { usedForFeature } from 'constants/logEvents'
import useBookingAttribution from 'hooks/useBookingAttribution'
import useFetchProvidersList from 'hooks/provider/useFetchProviders'
import LeadGenerationElements from 'app/ProviderSearch/LeadGenerationElements'
import { LeadGenProvider, LeadGenScenarios } from 'context/LeadGen'
// import SimpleInsuranceVerification from 'common/SimpleInsuranceVerification'
// import useGetUserTreatment from 'hooks/treatments/useGetUserTreatment'
// import { updateProviderSearchQueryUrl } from 'common/SimpleInsuranceVerification/updateProviderSearchQueryUrl'

const Footer = dynamic(() => import('components/common/Footer'))

const ChangeLocationModal = dynamic(
  () => import('components/app/ProviderSearch/ChangeLocationModal')
)

const PracticesMap = dynamic(
  () => import('app/ProviderSearch/ProvidersAndMap/PracticesMap'),
  {
    ssr: false,
    loading: () => (
      // Placeholder the size of the map until map isn't loaded
      <div className="available-page-height flex-fill position-sticky top-0 app-border-radius"></div>
    ),
  }
)

const SearchPage: React.FC<Omit<ProvidersListingPageProps, 'deviceType'>> = ({
  groupedFilters: initialGroupedFilters,
  filterLookup: initialFilterLookup,
  canonicalLink,
}) => {
  const { isSupportUser, isSupportOrThirdPartyUser } = useThriveAuth()
  const [showMobileFilterModal, setShowMobileFilterModal] = useState<boolean>(
    false
  )
  // Every filter value, for selections kept from a state that offers them
  const [allGroupedFilters] = useState<GroupedProviderFilter>(() => ({
    ...initialGroupedFilters,
    [ProviderFilterTypes.GENDER]: getRequiredGenderFilters(
      initialGroupedFilters[ProviderFilterTypes.GENDER]
    ),
  }))
  const [groupedFilters, setGroupedFilters] = useState<GroupedProviderFilter>(
    allGroupedFilters
  )
  const [filterLookup, setFilterLookup] = useState<ProviderFilterLookup>(
    initialFilterLookup
  )
  const latestFiltersRequestId = useRef(0)
  const [isMapModeActive, setIsMapModeActive] = useState<boolean>(false)

  const { phoneNumber, formattedPhoneNumber } = searchNumber
  // this variable is to handle zero provider state cases in waterfall flow
  const [isZeroProviderState, setIsZeroProviderState] = useState<boolean>(false)
  const [
    waterfallProviderInsuranceInfo,
    setWaterfallProviderInsuranceInfo,
  ] = useState<WaterfallProvidersInsuranceNWInfo>({
    noWaterfallProviderAcceptsInsurance: false,
    notInNWProvidersCount: 0,
  })

  const [isDowFallbackActive, setIsDowFallbackActive] = useState<boolean>(false)

  const isDirectMatch0WithOnlyPrimaryFilter = useRef(false)
  const previousLocationState = useRef<string>('')
  const previousSalesforceLocationHash = useRef<string>('')
  const previousPartnershipCode = useRef<string>('')
  const appliedBrowserLocationHash = useRef<string>('')
  const hasManuallySelectedLocation = useRef<boolean>(false)
  const router = useRouter()
  const { asPath } = router
  const query = getParsedQuery(router)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // TODO: Extract practice logic to PracticeMaps component
  const [practicesList, setPracticesList] = useState<PracticeProvider[]>([])
  const [waterfallPractices, setWaterfallPractices] = useState<
    PracticeProvider[]
  >([])
  const [hasWaterfallProviders, setHasWaterfallProviders] = useState(false)
  const [selectedPractice, setSelectedPractice] = useState<PracticeProvider>(
    null
  )
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false)

  const { timeZone, format, ...restTimeZone } = useTimeZoneContext()

  const {
    enabled: extendedBookingEnabled,
    isLoading: extendedBookingLoading,
  } = useExtendedBookingTreatment()

  const {
    providerList,
    providerListError,
    providerListLoading,
    providerListResult,
    intakeSupplyReserveData,
    providerPagination,
  } = useFetchProvidersList({
    isDirectMatch0WithOnlyPrimaryFilter,
    groupedFilters,
    filterLookup,
    setSelectedPractice,
    secondaryFiltes: SECONDARY_FILTERS,
    extendedBookingEnabled,
    extendedBookingLoading,
    timezoneContext: {
      timeZone,
      format,
      ...restTimeZone,
    },
  })

  useInitGlobalFilter(initialGroupedFilters)
  /*
    useB2bDetailsByKeyword will fetch the b2b details if there is any partner
    related keyword in url and set the partnership details for the end user
  */
  const { isB2bDetailsLoading, b2bDetailsError } = useB2bDetailsByKeyword()

  const {
    startSlotDate,
    slotListLimit,
    setStartSlotDate,
    setSlotListLimit,
  } = slotDate(timeZone)

  const bookingWindowHours = INTAKE_BOOKING_WINDOW_HOURS

  const {
    providerSlots,
    loading: slotsLoading,
    error: slotsError,
    nextAvailabilityForProviders,
  } = getProviderSlots({
    startSlotDate,
    slotListLimit,
    providerList,
    timeZone,
    availabilityFilters: groupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE],
    extendedBookingEnabled,
    bookingWindowHours,
  })

  const {
    lead,
    leadId,
    hasLeadId,
    isFetched: isLeadFetched,
    isSalesForceLeadFetchEnabled,
  } = useSalesforceLead()

  // set utm_medium into redux
  useBookingAttribution()

  const { sfAccount, caseId, hasCaseId } = useSalesforceCase()
  const showPrenav = !isSupportUser || hasLeadId || hasCaseId

  const isSalesForceB2BOn =
    useGetTreatment(TreatmentFlags.SALESFORCE_B2B_CASE_ACCOUNT) ===
    treatmentFlagValues.ON
  const sfCaseAutoFill =
    useGetTreatment(TreatmentFlags.SALESFORCE_CASE_AUTOFILL) ===
    treatmentFlagValues.ON

  const { isEnabled: isWaterfallSimplification } = useWaterfallSimplification()

  const showWaterFallTreatment = useMemo(() => {
    const parsedQuery = getParsedQuery(router)
    const isDowOnlyEmptyFallback = shouldEnableDowOnlyWaterfall(
      parsedQuery,
      SECONDARY_FILTERS,
      isDirectMatch0WithOnlyPrimaryFilter.current
    )

    if (
      !providerListLoading &&
      !isB2bDetailsLoading &&
      providerPagination?.count < WATERFALL_MOUNT_MAX_DIRECT_MATCHES &&
      (!isDirectMatch0WithOnlyPrimaryFilter.current ||
        isDowOnlyEmptyFallback) &&
      (hasSecondaryFilters(parsedQuery, SECONDARY_FILTERS) ||
        isDowOnlyEmptyFallback)
    ) {
      return true
    }

    return false
  }, [
    providerListLoading,
    providerPagination,
    isDirectMatch0WithOnlyPrimaryFilter.current,
    router,
    isB2bDetailsLoading,
  ])

  // How many partial matches the waterfall may auto-fill into the initial view.
  // `null` means unbounded, which is control's existing behaviour. In the variant
  // the initial view is capped at six providers in total, so a search with six or
  // more exact matches gets a budget of zero and only reaches partials on demand.
  const initialPartialBudget = useMemo(() => {
    if (!isWaterfallSimplification) {
      return null
    }

    return getInitialPartialBudget(providerList?.length ?? 0)
  }, [isWaterfallSimplification, providerList?.length])

  const leadGenScenario = useMemo(() => {
    const hasInsurance = query.insuranceId !== undefined
    const hasProviders = providerList?.length > 0

    if (hasProviders) {
      return LeadGenScenarios.SEARCH_LOW_SUPPLY
    }

    return hasInsurance
      ? LeadGenScenarios.SEARCH_NO_SUPPLY_WATERFALL_INSURANCE
      : LeadGenScenarios.SEARCH_NO_SUPPLY_WATERFALL
  }, [query.insuranceId, providerList?.length])

  // When location query params are incomplete, resolve location via browser
  // geolocation (prefer) or IP/Places (fallback) through useFetchLocation.
  const fetchLocationEnabled = useMemo(() => {
    const { location, latitude, longitude, state } = getParsedQuery(router)
    return !(location && latitude && longitude && state)
  }, [asPath])

  const {
    locationData,
    browserLocationData,
    hasOutsideUsBrowserLocation,
    loading: locationFetchLoading,
    status: locationFetchStatus,
  } = useFetchLocation({
    locationString: query.location as string,
    fetchLocationEnabled,
    isBrowserGeolocationPreferred: true,
    usedForFeature:
      usedForFeature.SearchPageAutoSelectCurrentLocationOrFromQuery,
  })

  // Persists the visitor's resolved state (manual search or geo-IP/browser
  // geolocation) so it survives navigation into the booking flow, where it's
  // compared against the patient's first booking state for the auto-switch
  // toast's analytics property. Search is the only place this state is ever
  // resolved, so it's captured here rather than threaded through every
  // "start booking" call site.
  useEffect(() => {
    const resolvedState =
      (query.state as string) ||
      locationData?.state ||
      browserLocationData?.state

    // sessionStorage can throw in restricted contexts (e.g. Safari private
    // browsing, sandboxed iframes) — search should continue either way.
    try {
      if (resolvedState) {
        sessionStorage.setItem(
          COOKIE_KEYS.PATIENT_GEOLOCATION_STATE,
          resolvedState
        )
      } else {
        sessionStorage.removeItem(COOKIE_KEYS.PATIENT_GEOLOCATION_STATE)
      }
    } catch {
      // no-op: geolocation state is best-effort analytics context
    }
  }, [query.state, locationData?.state, browserLocationData?.state])

  const { isCustomSize: showMapOnDesktopScreen } = useBreakpoint({
    minWidth: 1080,
  })

  const {
    isMobileScreen,
    isLargeSize,
    isExtraLargeSize,
    isAboveXLSize,
  } = useBreakpoint()
  const isDesktopView = isExtraLargeSize || isAboveXLSize

  const shouldShowMap = useMemo(
    () =>
      isMapModeActive &&
      showMapOnDesktopScreen &&
      (!!providerList?.length ||
        !!query.practiceId ||
        (!!query.latitude && !!query.longitude)),
    [
      isMapModeActive,
      showMapOnDesktopScreen,
      providerList?.length,
      query.practiceId,
      query.latitude,
      query.longitude,
    ]
  )

  const shouldShowZeroStateWithMap = useMemo(
    () => isZeroProviderState && shouldShowMap && isSupportOrThirdPartyUser,
    [isZeroProviderState, shouldShowMap, isSupportOrThirdPartyUser]
  )

  const headerPositionStyle = useHeaderScrollAnimate({
    animation: {
      hide: 'translateY(-223px)',
      show: 'translateY(0px)',
    },
  })

  //=============================================================Use Effects
  useEffect(() => {
    const currentSearchQuery = getParsedQuery(router)
    const previousSearchedQuery = JSON.parse(
      sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
    )
    const isSalesforceCase = hasCaseId
    const previousCode = previousSearchedQuery?.partnershipCode || ''
    const SalesforceCase = isSalesforceCase ? '' : previousCode

    previousPartnershipCode.current = isSalesForceB2BOn
      ? SalesforceCase
      : previousCode

    sessionStorage.setItem(
      sessionStorageKeys.PROVIDER_SEARCH_QUERY,
      JSON.stringify(
        _omit(currentSearchQuery, [
          ...supportDashboardToProviderSearchKeys,
          !isSalesForceLeadFetchEnabled && 'leadId',
          sessionStorageKeys.IS_ACCEPTED_MEDICAID_SESSION,
        ])
      )
    )

    if (currentSearchQuery.isAcceptedMedicaidSession !== undefined) {
      sessionStorage.setItem(
        sessionStorageKeys.IS_ACCEPTED_MEDICAID_SESSION,
        currentSearchQuery.isAcceptedMedicaidSession
      )
    }
  }, [asPath])

  useEffect(() => {
    const qsQuery = getParsedQuery(router)
    const queryState = qsQuery.state

    const SfAddress =
      lead?.Address || (sfCaseAutoFill && sfAccount?.PersonMailingAddress)
    const salesforceLocationHash = SfAddress ? hashSfAddress(SfAddress) : ''
    const hasSalesforceAddress = SfAddress
    let salesforceAddressChanged =
      hasSalesforceAddress &&
      previousSalesforceLocationHash.current &&
      previousSalesforceLocationHash.current !== salesforceLocationHash

    if (hasSalesforceAddress && !previousSalesforceLocationHash.current) {
      previousSalesforceLocationHash.current = salesforceLocationHash
    }

    if (
      sfCaseAutoFill &&
      sfAccount?.PersonMailingAddress &&
      salesforceAddressChanged
    ) {
      salesforceAddressChanged = false
    }

    const userSetLocationChanged =
      qsQuery.state && previousLocationState.current !== queryState

    if (userSetLocationChanged) {
      filtersAPICall(queryState)
    }

    previousLocationState.current = queryState

    /*
     * If either of location, state, lat, long is missing
     * if its present then it would take the value from the session storage and use it in the queryParams
     * else it would show the location popup
     */

    const isQueryLocationDataIncomplete =
      !qsQuery?.location ||
      !qsQuery?.state ||
      !qsQuery?.latitude ||
      !qsQuery?.longitude

    const browserLocationHash = browserLocationData
      ? `${browserLocationData.lat},${browserLocationData.lng},${browserLocationData.state}`
      : ''
    const hasNewBrowserLocation =
      !!browserLocationHash &&
      appliedBrowserLocationHash.current !== browserLocationHash
    const shouldApplyBrowserLocation =
      hasNewBrowserLocation && !hasManuallySelectedLocation.current

    const individualCounselingFilter = groupedFilters[
      ProviderFilterTypes.COUNSELING_TYPE
    ]?.find((filter) => filter.value === CounselingTypeEnum.INDIVIDUAL)
    let counselingTypeFilter: string[] = null

    if (!_isEmpty(qsQuery.counselingType)) {
      counselingTypeFilter = qsQuery.counselingType as string[]
    } else if (individualCounselingFilter?.id) {
      counselingTypeFilter = [individualCounselingFilter.id]
    }

    const modifiedQuery = _omit(qsQuery, [
      'latitude',
      'longitude',
      'state',
      'location',
      'userId',
      'patients',
    ])

    if (
      !!previousPartnershipCode.current &&
      !qsQuery.partnershipCode &&
      _isEmpty(qsQuery)
    ) {
      modifyURLQuery(router, {
        partnershipCode: previousPartnershipCode.current,
      })
    }

    // If the Salesforce lead address has changed and the user has not manually changed the location,
    // then update the provider search with the new Salesforce lead address.
    // hide the location modal if it is open since we have a location

    if (salesforceAddressChanged && !userSetLocationChanged) {
      previousSalesforceLocationHash.current = salesforceLocationHash
      const newQuery = {
        ...modifiedQuery,
        state: SfAddress?.state,
        latitude: SfAddress?.latitude?.toString(),
        longitude: SfAddress?.longitude?.toString(),
        location: SfAddress?.city ?? SfAddress?.state ?? SfAddress?.postalCode,
      }
      modifyURLQuery(
        router,
        {
          ...newQuery,
          limit: PAGINATION_LIMIT.toString(),
          offset: modifiedQuery.offset || '0',
          ...(qsQuery.userId && { userId: qsQuery.userId }),
          ...(qsQuery.patients && { patients: qsQuery.patients }),
          ...(counselingTypeFilter && {
            counselingType: counselingTypeFilter,
          }),
        },
        true,
        'replace'
      )

      setShowLocationModal(false)
      return
    }

    // If the location data in the query is incomplete (i.e., either the location, state, latitude, or longitude is missing),
    // this block of code will attempt to complete the missing data.
    // There are three scenarios handled here:
    // 1. If the latitude and longitude are missing but we have location data available,
    //    the code will use the user's current location data to fill in the missing latitude, longitude, state, and location.
    // 2. If there are no query parameters related to location, the code will check for previously stored parameters
    //    and use those to fill in the missing data.
    // 3. If neither of the above conditions are met, the function will return without making any changes.
    // After determining the new query parameters, the code will call the `modifyURLQuery` function to update the URL
    // with the new query parameters. This will trigger a new search with the updated location data.
    // The user experience here is that if they have incomplete location data in their search,
    // the application will attempt to automatically complete it using either their current location or previously stored data.
    // If neither is available, the search will not be performed and the user will need to manually input the location data.

    if (hasOutsideUsBrowserLocation && !hasManuallySelectedLocation.current) {
      setShowLocationModal(true)
      return
    }

    if (locationFetchLoading) {
      setShowLocationModal(false)
      return
    }

    if (isQueryLocationDataIncomplete || shouldApplyBrowserLocation) {
      const resolvedLocationData = shouldApplyBrowserLocation
        ? browserLocationData
        : locationData

      if (resolvedLocationData) {
        // WHen a location query is set without other parameter
        // Displays from users current location data
        setShowLocationModal(false)

        if (shouldApplyBrowserLocation) {
          appliedBrowserLocationHash.current = browserLocationHash
        }

        const newQuery = {
          ...modifiedQuery,
          latitude: resolvedLocationData.lat,
          longitude: resolvedLocationData.lng,
          state: resolvedLocationData.state,
          location: resolvedLocationData.formatted_address,
        }

        modifyURLQuery(
          router,
          {
            ...newQuery,
            limit: PAGINATION_LIMIT.toString(),
            offset: modifiedQuery.offset || '0',
            ...(qsQuery.userId && { userId: qsQuery.userId }),
            ...(qsQuery.patients && { patients: qsQuery.patients }),
            ...(counselingTypeFilter && {
              counselingType: counselingTypeFilter,
            }),
          },
          true,
          'replace'
        )
        return
      }

      if (hasSalesforceAddress) {
        return
      }

      setShowLocationModal(true)
    }
  }, [
    asPath,
    isLeadFetched,
    locationData,
    browserLocationData,
    hasOutsideUsBrowserLocation,
    locationFetchLoading,
    lead?.Address,
    sfAccount?.PersonMailingAddress,
  ])

  //============================================================= Filters Api call
  const filtersAPICall = async (state?: string | null) => {
    const requestId = ++latestFiltersRequestId.current

    try {
      const [response, stateScopedGenderFilters] = await Promise.all([
        getFilters(state),
        state
          ? getFiltersByTypeAndState(ProviderFilterTypes.GENDER, state).catch(
              (): null => null
            )
          : Promise.resolve(null),
      ])

      if (requestId !== latestFiltersRequestId.current) {
        return
      }

      const stateScopedGroupedFilters = groupFilters(response.result)
      const groupedFilters = {
        ...stateScopedGroupedFilters,
        [ProviderFilterTypes.GENDER]: mergeGenderFiltersWithStateAvailability(
          initialGroupedFilters[ProviderFilterTypes.GENDER],
          stateScopedGenderFilters
        ),
      }
      const filterLookup = getFilterLookup(response.result)

      setGroupedFilters(groupedFilters)
      setFilterLookup(filterLookup)
    } catch {
      // Keep the existing filters when the general state-filter request fails.
    }
  }

  //============================================================= Page functions
  const onChangeLocation = (locationData: ChangeLocationFormProps) => {
    hasManuallySelectedLocation.current = true
    const { partnershipCode, counselingType, ...restQuery } = getParsedQuery(
      router
    )
    const { location, state, latitude, longitude } = locationData
    const allCounselingTypeFilters =
      groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]
    const isCounselingTypeInFilters = allCounselingTypeFilters?.find(
      (filter) => filter.id === counselingType?.[0]
    )
    const individualCounselingFilter = groupedFilters[
      ProviderFilterTypes.COUNSELING_TYPE
    ]?.find((filter) => filter.value === CounselingTypeEnum.INDIVIDUAL)
    const counselingTypeId = isCounselingTypeInFilters
      ? counselingType?.[0]
      : individualCounselingFilter.id
    const newQuery = {
      ...restQuery,
      state,
      latitude,
      longitude,
      location,
      ...(isSalesForceB2BOn
        ? { sfId: leadId ? leadId : caseId || undefined }
        : { leadId: leadId ? leadId : undefined }),
    }

    modifyURLQuery(
      router,
      {
        ...newQuery,
        limit: PAGINATION_LIMIT.toString(),
        offset: '0',
        ...(counselingTypeId && {
          counselingType: [counselingTypeId],
        }),
        ...(!!partnershipCode && { partnershipCode }),
        ...(isSalesForceB2BOn
          ? query?.sfId && isSalesForceLeadFetchEnabled && { sfId: query.sfId }
          : query?.leadId &&
            isSalesForceLeadFetchEnabled && { leadId: query.leadId }),
      },
      true,
      'replace'
    )
  }

  const isEmptyQuery = _isEmpty(query)

  const isInPersonSelected = useMemo(() => {
    const [availabilityFilterQuery] = query['availabilityType'] || []
    const inPersonFilter = initialGroupedFilters[
      ProviderFilterTypes.AVAILABILITY_TYPE
    ].find((filter) => filter.value === AvailabilityFilterValues.inPerson)

    return availabilityFilterQuery === inPersonFilter.id
  }, [query, initialGroupedFilters])
  const slotDateValue: SlotDateType = {
    startSlotDate,
    slotListLimit,
    setStartSlotDate,
    setSlotListLimit,
  }

  const providerSlotValue: ProviderScheduleDetails = {
    nextAvailabilityForProviders,
    providerSlots,
    loading: slotsLoading,
    error: slotsError,
  }

  const { countDetails, providerCardDetails } = useShownSlots(
    SLOTS_ROW_LIMIT_V3,
    providerList || [],
    slotDateValue,
    providerSlotValue,
    timeZone,
    extendedBookingEnabled
  )
  const userSelectedFilters = getFilterValueFromId(groupedFilters, query)

  const logMapRudderstackEvent = (isMapModeActive?: boolean) => {
    const eventName = isMapModeActive
      ? RudderEvent.BOOKING_TOOL_SHOW_MAP_CLICKED
      : RudderEvent.BOOKING_TOOL_HIDE_MAP_CLICKED
    logEvent(eventName, {
      filters: {
        age: userSelectedFilters?.client_age_group || 'null',
        appointment_type: userSelectedFilters?.availability_type || 'null',
        counseling_approach: userSelectedFilters?.counseling_approach || 'null',
        gender: userSelectedFilters?.gender || 'null',
        issues: userSelectedFilters?.issues || 'null',
        language: userSelectedFilters?.language || 'null',
        payment_type: query.insuranceId
          ? sessionStorage.getItem(sessionStorageKeys.INSURANCE_PROVIDER_NAME)
          : 'Self Pay',
        therapy_type: userSelectedFilters?.counseling_type || 'null',
      },
      total_providers_available: providerCardDetails?.length,
      count_provider_slots_shown: countDetails.slot_sum,
    })
  }

  const onMapShowHideButtonClick = (isMapModeActive: boolean) => {
    const showMap = !isMapModeActive
    setIsMapModeActive(showMap)
    !slotsLoading && logMapRudderstackEvent(showMap)
  }

  // const coeInsuranceWidgetFlag = useGetUserTreatment(
  //   'COE_CAN_USE_INSURANCE_VERIFICATION_ON_SEARCH_PAGE'
  // )
  // const canSeeInsuranceVerificationWidget =
  //   isSupportUser && coeInsuranceWidgetFlag === treatmentFlagValues.ON

  useEffect(() => {
    setIsZeroProviderState((prev) => {
      return (
        ((!providerList?.length && !showWaterFallTreatment) ||
          (!providerList?.length && showWaterFallTreatment && prev)) &&
        !providerListLoading
      )
    })
  }, [providerList?.length, showWaterFallTreatment, providerListLoading])

  // Reset the DOW-fallback flag on every new search so a stale `true` from a
  // previous navigation cannot corrupt the date-bar for a new search that has
  // its own active DOW filter.
  useEffect(() => {
    setIsDowFallbackActive(false)
  }, [router.asPath])

  const isLowAvailability = useMemo(() => {
    if (showWaterFallTreatment || providerListLoading) {
      return false
    }

    return (
      providerPagination.count &&
      (providerPagination.count <= LOW_AVAILABILITY_MAX_PROVIDERS ||
        (providerPagination.count <=
          LOW_AVAILABILITY_WITH_FEW_SLOTS_MAX_PROVIDERS &&
          countDetails.provider_count.every(
            (slotCount) =>
              Object.values(slotCount)[0] <=
              LOW_AVAILABILITY_MAX_SLOTS_PER_PROVIDER
          )))
    )
  }, [providerList, showWaterFallTreatment, countDetails])

  const showLeadGenElements =
    (showWaterFallTreatment || isLowAvailability) && !isSupportOrThirdPartyUser

  // Low-availability has no waterfall control above "Stay updated", so it lacks
  // the large gap the waterfall gets. Add it here so "Stay updated" is always
  // separated from the search results, matching the waterfall spacing.
  const addLowAvailabilitySpacing = !!isLowAvailability

  return (
    <HeadComponent
      title="Thriveworks Therapy - Find and Book sessions with Providers"
      content="At Thriveworks find the best Providers at your preferred nearest location and book an online counselling or onsite therapy session"
      canonicalLink={canonicalLink}
    >
      <TimeZoneContext.Provider
        value={{
          timeZone,
          format,
          ...restTimeZone,
        }}
      >
        {/* {canSeeInsuranceVerificationWidget ? (
          <>
            <SimpleInsuranceVerification
              isHideable={true}
              getInitialValuesFromSalesforce={true}
              styling={{
                // Because using position: fixed, left & bottom need to take padding & margin into account.
                containerClassName: '~fixed ~z-10 ~bottom-4 ~left-3',
              }}
              initialValues={{
                stateLocation: getParsedQuery(router)?.['state'],
                carrierId: getParsedQuery(router)?.['insuranceId'],
              }}
              overrideStateLocation={getParsedQuery(router)?.['state']}
              overrideCarrierId={getParsedQuery(router)?.['insuranceId']}
              onVerifyComplete={({ carrier }) => {
                updateProviderSearchQueryUrl({
                  router,
                  newQueries: { insuranceId: carrier.id },
                })
                sessionStorage.setItem(
                  sessionStorageKeys.INSURANCE_PROVIDER_NAME,
                  carrier.name
                )
              }}
            />
          </>
        ) : null} */}
        <AllFiltersProvider allGroupedFilters={allGroupedFilters}>
          <MobileFilterModalProvider
            showMobileFilterModal={showMobileFilterModal}
            setShowMobileFilterModal={setShowMobileFilterModal}
          >
            <PageLoader
              loadingMessage=""
              loadingContainerClass="d-flex flex-fill h-100"
              isLoading={
                locationFetchLoading &&
                locationFetchStatus !== LOCATION_FETCH_STATUS.empty
              }
            >
              <div
                className={classNames(styles.search_page_container, {
                  [styles.search_page_container_support]: isSupportOrThirdPartyUser,
                })}
              >
                <div className={styles.header_container}>
                  <animated.div
                    style={headerPositionStyle}
                    className={classNames(styles.navbar_container, {
                      [styles.navbar_container_with_pre_nav]: showPrenav,
                    })}
                  >
                    <Navbar
                      navbarContainerStyle={classNames(
                        styles.searchpage_navbar,
                        {
                          [styles.dropdown_shadow]: !(
                            isMobileScreen || isLargeSize
                          ),
                        }
                      )}
                      hideBookSessionButton
                    >
                      {/* This filter is only displayed on desktop */}
                      {isDesktopView && (
                        <HeaderFilter groupedFilters={groupedFilters} />
                      )}
                    </Navbar>

                    <div className={styles.provider_filter_container}>
                      {/* Hiding the shadow on page scroll due to THRIV-3267, can come back so commenting this instead */}
                      {/* <div
                    className={classNames(
                      styles.sticky_container,
                      {
                        [styles.sticky_container_shadow]: pageScrolled,
                      },
                      'd-flex flex-column bg-white'
                    )}
                  > */}
                      {!isDesktopView && (
                        <div className="pb-3">
                          <HeaderFilter groupedFilters={groupedFilters} />
                        </div>
                      )}
                      {/* Filter container */}
                      <ScrollHorizontally
                        containerClass={classNames(
                          styles.overflow_unset_on_lg,
                          'w-100 mb-3 justify-content-center font-gibson'
                        )}
                        childrenClass={classNames(
                          styles.overflow_unset_on_lg,
                          'd-flex px-4 pb-2 pt-4'
                        )}
                      >
                        <ProviderFilter
                          groupedFilters={groupedFilters}
                          isInPersonSelected={isInPersonSelected}
                          isExtendedBookingEnabled={extendedBookingEnabled}
                        />
                      </ScrollHorizontally>
                      {isSupportOrThirdPartyUser && <AvailabilityLegend />}
                    </div>
                  </animated.div>
                  {showPrenav ? (
                    <Prenav
                      callUsBannerNumber={{
                        number: phoneNumber,
                        formattedNumber: formattedPhoneNumber,
                      }}
                      containerStyle={classNames(styles.prenav_container, {
                        [styles.lower_zindex]: showMobileFilterModal,
                      })}
                    />
                  ) : null}
                </div>

                {showLocationModal && (
                  <ChangeLocationModal
                    handleSubmit={onChangeLocation}
                    groupedFilters={groupedFilters}
                    show={showLocationModal}
                    onHide={() => {
                      setShowLocationModal(false)
                    }}
                  />
                )}
                <div
                  ref={scrollContainerRef}
                  // Hiding the shadow on page scroll due to THRIV-3267, can come back so commenting this instead
                  // onScroll={onProviderListScroll}
                  className={styles.scrollable_content_section}
                >
                  <LeadGenProvider
                    groupedFilters={groupedFilters}
                    initialScenario={leadGenScenario}
                  >
                    <SlotDateContext.Provider
                      value={{
                        startSlotDate,
                        slotListLimit,
                        setStartSlotDate,
                        setSlotListLimit,
                      }}
                    >
                      <ProviderSlotContext.Provider
                        value={{
                          nextAvailabilityForProviders,
                          providerSlots,
                          loading: slotsLoading,
                          error: slotsError,
                        }}
                      >
                        {!isEmptyQuery ? (
                          <div
                            className={classNames({
                              [styles.SearchList_container]:
                                !isZeroProviderState ||
                                shouldShowZeroStateWithMap,
                              [styles.SearchList_container_support]: isSupportOrThirdPartyUser,
                            })}
                          >
                            <div
                              className={classNames({
                                [styles.SearchList_container_content_withMap]:
                                  (!isZeroProviderState && shouldShowMap) ||
                                  shouldShowZeroStateWithMap,
                                [styles.SearchList_container_content_withoutMap]:
                                  !isZeroProviderState && !shouldShowMap,
                                [styles.SearchList_container_content_zero_providers]:
                                  isZeroProviderState &&
                                  !shouldShowZeroStateWithMap,
                              })}
                            >
                              <ProvidersList
                                scrollContainer={scrollContainerRef?.current}
                                groupedFilters={groupedFilters}
                                extendedBookingEnabled={extendedBookingEnabled}
                                extendedBookingLoading={extendedBookingLoading}
                                providerListLoading={
                                  providerListLoading ||
                                  isB2bDetailsLoading ||
                                  extendedBookingLoading
                                }
                                providerList={providerList}
                                onSetPracticesList={setPracticesList}
                                selectedPractice={selectedPractice}
                                onSelectPractice={setSelectedPractice}
                                providerPagination={providerPagination}
                                shouldFetchPractices={
                                  query.practiceId ||
                                  (showMapOnDesktopScreen && isMapModeActive)
                                }
                                providerCardDetails={providerCardDetails}
                                countDetails={countDetails}
                                inPersonFilterSelected={isInPersonSelected}
                                isWaterfallFlow={showWaterFallTreatment}
                                showWaterfallDivider={
                                  !isWaterfallSimplification ||
                                  hasWaterfallProviders
                                }
                                isZeroProviderState={isZeroProviderState}
                                providerListError={
                                  providerListError || b2bDetailsError
                                }
                                providerListResult={providerListResult}
                                intakeSupplyReserveData={
                                  intakeSupplyReserveData
                                }
                                waterfallProviderInsuranceInfo={
                                  waterfallProviderInsuranceInfo
                                }
                                showEmptyScreen={isZeroProviderState}
                                dowFallbackActive={isDowFallbackActive}
                              />

                              {showWaterFallTreatment && (
                                <ProvidersListWaterfall
                                  scrollContainer={scrollContainerRef?.current}
                                  groupedFilters={groupedFilters}
                                  filterLookup={filterLookup}
                                  providerList={providerList}
                                  selectedPractice={selectedPractice}
                                  inPersonFilterSelected={isInPersonSelected}
                                  countDetails={countDetails}
                                  onSetIsZeroProviderState={
                                    setIsZeroProviderState
                                  }
                                  noWaterfallProviderAcceptsInsurance={
                                    waterfallProviderInsuranceInfo.noWaterfallProviderAcceptsInsurance
                                  }
                                  setNoWaterfallProviderAcceptsInsurance={
                                    setWaterfallProviderInsuranceInfo
                                  }
                                  onSetWaterfallPractices={
                                    setWaterfallPractices
                                  }
                                  onSetHasWaterfallProviders={
                                    setHasWaterfallProviders
                                  }
                                  extendedBookingEnabled={
                                    extendedBookingEnabled
                                  }
                                  isWaterfallSimplification={
                                    isWaterfallSimplification
                                  }
                                  initialPartialBudget={initialPartialBudget}
                                  onSetDowFallbackActive={
                                    setIsDowFallbackActive
                                  }
                                />
                              )}
                            </div>

                            {shouldShowMap && (
                              <div className="flex-grow-1">
                                <PracticesMap
                                  practicesList={
                                    practicesList?.length > 0
                                      ? practicesList
                                      : waterfallPractices
                                  }
                                  selectedPractice={selectedPractice}
                                  isMapModeActive={isMapModeActive}
                                  onSelectPractice={(practice) => {
                                    setSelectedPractice(practice)
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="h-80"></div>
                        )}
                      </ProviderSlotContext.Provider>
                    </SlotDateContext.Provider>
                    {showLeadGenElements ? (
                      <LeadGenerationElements
                        isWaterfallTreatment={showWaterFallTreatment}
                        hasLowAvailabilitySpacing={addLowAvailabilitySpacing}
                      />
                    ) : (
                      providerList?.length !== 0 && <Footer />
                    )}
                  </LeadGenProvider>
                </div>
                {isSupportOrThirdPartyUser && showMapOnDesktopScreen && (
                  <ShowHideMapButton
                    isMapModeActive={isMapModeActive}
                    handleOnClick={() =>
                      onMapShowHideButtonClick(isMapModeActive)
                    }
                    className={styles.addMarginBottom}
                  />
                )}
                {isSupportOrThirdPartyUser && <ResetSupportBooking />}
              </div>
            </PageLoader>
          </MobileFilterModalProvider>
        </AllFiltersProvider>
      </TimeZoneContext.Provider>
    </HeadComponent>
  )
}

export default SearchPage
